/**
 * KillListEntryDetail — single kill-list entry view.
 *
 * Reachable via `/kill-list/:entryId` — same URL the adaptive card's
 * "Open in Panel" deep-link uses. Mirrors ROP-teams' RKL EntryDetail
 * verify/execute/cancel surface, but rendered against PCB SysOps shell.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import type { KillListEntryDto } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocal } from '@/utils/time';

// Kill order (Bill 2026-09-15): dependents first. Core Director refuses to modify
// a record whose dependency is already gone (a card whose account purged answers
// "no Accounts are linked to the ATM Card").
const KILL_ORDER_LABEL: Record<number, string> = {
  1: '1 of 4 — NetTeller (always first)',
  2: '2 of 4 — Debit card (F103=H, F262=41)',
  3: '3 of 4 — Account (after its cards)',
  4: '4 of 4 — CIF (always last)',
};

function lastOutcome(entry: KillListEntryDto): string | null {
  if (!entry.metadataJson) return null;
  try {
    const meta = JSON.parse(entry.metadataJson) as { execution?: { detail?: string; routedToSysOps?: boolean; confirmedBySysOps?: boolean } };
    const ex = meta.execution;
    if (!ex?.detail) return null;
    return ex.routedToSysOps ? `Needs SysOps: ${ex.detail}` : ex.detail;
  } catch {
    return null;
  }
}

// Test-account cleanup (Bill 2026-09-15): a held entry waits for the officer's
// Done testing or its auto-release date; executing it early is refused.
function heldUntil(entry: KillListEntryDto): string | null {
  if (!entry.metadataJson) return null;
  try {
    const meta = JSON.parse(entry.metadataJson) as { testCleanup?: { released?: boolean; releaseAfterUtc?: string } };
    const tc = meta.testCleanup;
    if (!tc || tc.released) return null;
    const when = tc.releaseAfterUtc ? formatLocal(tc.releaseAfterUtc) : 'the auto-release date';
    return `Test account still in use. Cleanup starts when the officer clicks Done testing, or on ${when}.`;
  } catch {
    return null;
  }
}

// An officer's request carries { note } in the reason details.
function requestNote(entry: KillListEntryDto): string | null {
  if (!entry.reasonDetailsJson) return null;
  try {
    const d = JSON.parse(entry.reasonDetailsJson) as { note?: string };
    return d.note?.trim() ? d.note.trim() : null;
  } catch {
    return null;
  }
}

export function KillListEntryDetail(): JSX.Element {
  const { entryId } = useParams();
  const api = useApiClient();
  const { user } = useAuth();
  const [entry, setEntry] = useState<KillListEntryDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [showVerify, setShowVerify] = useState<boolean>(false);
  const [verifyNote, setVerifyNote] = useState<string>('');
  const [showExecute, setShowExecute] = useState<boolean>(false);
  const [showSysOpsDone, setShowSysOpsDone] = useState<boolean>(false);
  const [showCancel, setShowCancel] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!entryId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.retail.killList.get(entryId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!resp.success) {
        setError(resp.error ?? 'Failed to load entry');
        setEntry(null);
      } else {
        setEntry(resp.entry ?? null);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load entry');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [api, entryId]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  const onVerify = useCallback(async (): Promise<void> => {
    if (!entryId || !user) return;
    setBusy(true);
    try {
      await api.retail.killList.verify(entryId, {
        verifiedByEmail: user.upn,
        approvalNote: verifyNote.trim() || undefined,
      });
      setShowVerify(false);
      setVerifyNote('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verify failed');
    } finally {
      setBusy(false);
    }
  }, [api, entryId, load, user, verifyNote]);

  const onExecute = useCallback(async (): Promise<void> => {
    if (!entryId || !user) return;
    setBusy(true);
    try {
      await api.retail.killList.execute(entryId, { executedByEmail: user.upn });
      setShowExecute(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Execute failed');
    } finally {
      setBusy(false);
    }
  }, [api, entryId, load, user]);

  // SysOps finished a NetTeller (MNTUSR) or debit-card (H / 41) kill in the
  // Core Director terminal — record it so the accounts and CIF can proceed.
  const onSysOpsDone = useCallback(async (): Promise<void> => {
    if (!entryId || !user) return;
    setBusy(true);
    try {
      await api.retail.killList.execute(entryId, { executedByEmail: user.upn, sysOpsConfirmed: true });
      setShowSysOpsDone(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not mark the entry done');
    } finally {
      setBusy(false);
    }
  }, [api, entryId, load, user]);

  const onCancel = useCallback(async (): Promise<void> => {
    if (!entryId || !user) return;
    const reason = cancelReason.trim();
    if (!reason) return;
    setBusy(true);
    try {
      await api.retail.killList.cancel(entryId, {
        cancelledByEmail: user.upn,
        reason,
      });
      setShowCancel(false);
      setCancelReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  }, [api, cancelReason, entryId, load, user]);

  return (
    <>
      <Link to="/kill-list" className="back-link">
        <ChevronLeft size={14} /> Back to Kill List
      </Link>

      {loading && <p>Loading…</p>}

      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}

      {entry && (
        <article className="card">
          <header className="card-header">
            <h2>Kill entry {entry.killListEntryId.slice(0, 8)}…</h2>
            <span
              className={
                entry.status === 'executed'
                  ? 'pill pill--ok'
                  : entry.status === 'cancelled'
                    ? 'pill pill--muted'
                    : entry.status === 'failed'
                      ? 'pill pill--err'
                      : 'pill pill--warn'
              }
            >
              {entry.status}
            </span>
            {entry.testMode && <span className="pill pill--warn">Test Mode</span>}
          </header>

          <dl className="kv-grid">
            <dt>Entry type</dt>
            <dd>{entry.entryType}</dd>
            <dt>Target</dt>
            <dd>
              <code>{entry.targetId}</code>
            </dd>
            {entry.killOrder != null && (
              <>
                <dt>Kill order</dt>
                <dd>{KILL_ORDER_LABEL[entry.killOrder] ?? '—'}</dd>
              </>
            )}
            {lastOutcome(entry) && (
              <>
                <dt>Last outcome</dt>
                <dd>{lastOutcome(entry)}</dd>
              </>
            )}
            {heldUntil(entry) && (
              <>
                <dt>Held</dt>
                <dd>{heldUntil(entry)}</dd>
              </>
            )}
            {entry.customerName && (
              <>
                <dt>Customer</dt>
                <dd>{entry.customerName}</dd>
              </>
            )}
            {requestNote(entry) && (
              <>
                <dt>Officer&rsquo;s note</dt>
                <dd>{requestNote(entry)}</dd>
              </>
            )}
            <dt>Reason</dt>
            <dd>{entry.reason}</dd>
            <dt>Source cart</dt>
            <dd>
              <code>{entry.sourceCartId ?? '—'}</code>
            </dd>
            <dt>Created</dt>
            <dd>
              {formatLocal(entry.createdAt)} by {entry.createdBy}
            </dd>
            {entry.verifiedAt && (
              <>
                <dt>Verified</dt>
                <dd>
                  {formatLocal(entry.verifiedAt)} by {entry.verifiedBy ?? '—'}
                </dd>
              </>
            )}
            {entry.executedAt && (
              <>
                <dt>Executed</dt>
                <dd>
                  {formatLocal(entry.executedAt)} by {entry.executedBy ?? '—'}
                </dd>
              </>
            )}
            {entry.reEntryOfEntryId && (
              <>
                <dt>Re-entry</dt>
                <dd>
                  This record was already killed
                  {entry.reEntryPriorExecutedAt ? ` on ${formatLocal(entry.reEntryPriorExecutedAt)}` : ''} by
                  entry <code>{entry.reEntryOfEntryId.slice(0, 8)}…</code>. The number was reused, or the
                  first kill never took. Check Core Director before executing again.
                </dd>
              </>
            )}
            {entry.cancelledAt && (
              <>
                <dt>Cancelled</dt>
                <dd>
                  {formatLocal(entry.cancelledAt)} by {entry.cancelledBy ?? '—'}
                </dd>
              </>
            )}
            {entry.notes && (
              <>
                <dt>Notes</dt>
                <dd>{entry.notes}</dd>
              </>
            )}
            {entry.approvalNote && (
              <>
                <dt>Approval note</dt>
                <dd>{entry.approvalNote}</dd>
              </>
            )}
          </dl>

          <div className="action-row">
            {entry.status === 'pending-verification' && !entry.testMode && (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setShowVerify(true)}
                disabled={busy}
              >
                Verify
              </button>
            )}
            {(entry.status === 'verified' ||
              (entry.testMode && entry.status !== 'executed')) && (
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => setShowExecute(true)}
                disabled={busy}
              >
                Execute
              </button>
            )}
            {entry.status === 'verified' && (entry.targetType === 'NT' || entry.targetType === 'ATM') && (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setShowSysOpsDone(true)}
                disabled={busy}
                title="SysOps completed this kill in the Core Director terminal"
              >
                Mark done in Core Director
              </button>
            )}
            {entry.status !== 'executed' && entry.status !== 'cancelled' && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setShowCancel((v) => !v)}
                disabled={busy}
              >
                Cancel entry
              </button>
            )}
          </div>

          {showCancel && (
            <div className="card card--inset">
              <label className="form-field">
                <span className="form-field__label">Cancel reason (required)</span>
                <textarea
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </label>
              <div className="action-row">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setShowCancel(false)}
                  disabled={busy}
                >
                  Never mind
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void onCancel()}
                  disabled={busy || !cancelReason.trim()}
                >
                  Cancel entry
                </button>
              </div>
            </div>
          )}
        </article>
      )}

      {showVerify && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>Verify kill entry</h3>
            <p>
              Marks this entry as verified by you. Execute is a separate
              action — you can change your mind before clicking Execute.
            </p>
            <label className="form-field">
              <span className="form-field__label">Approval note (optional)</span>
              <textarea
                rows={3}
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
              />
            </label>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setShowVerify(false)}
                disabled={busy}
              >
                Never mind
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void onVerify()}
                disabled={busy}
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showExecute && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>Execute kill</h3>
            <p>
              This runs the destructive action immediately and cannot be
              undone. Continue?
            </p>
            <p className="muted">
              Kill order is NetTeller, then debit card, then accounts, then the CIF.
              If something on this customer has to go first, you will be told exactly what.
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setShowExecute(false)}
                disabled={busy}
              >
                Never mind
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void onExecute()}
                disabled={busy}
              >
                {busy ? 'Executing…' : 'Execute'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSysOpsDone && entry && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>Mark done in Core Director</h3>
            <p>
              {entry.targetType === 'NT'
                ? 'Confirm the NetTeller user was deleted via MNTUSR (reason 5 = Test/Incomplete ID).'
                : 'Confirm F103 = H and F262 = 41 are set on this card. PPTCLN removes it on the 1st of the month.'}
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setShowSysOpsDone(false)}
                disabled={busy}
              >
                Never mind
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void onSysOpsDone()}
                disabled={busy}
              >
                {busy ? 'Saving…' : 'Yes, it is done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
